import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Download, FileSpreadsheet, FileJson, FileCode, File, FilterX, ChevronsUpDown, Check } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";

const API = process.env.REACT_APP_BACKEND_URL;

const VehiculosClasificador = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({});
  const [codigosPresupuestarios, setCodigosPresupuestarios] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [clasFilters, setClasFilters] = useState({ codigo: "", tipo: "", oficina: "", tasa: "", extranet: "", descuento: "" });

  const fetchClasificador = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/clasificador`, { headers });
      setItems(r.data);
    } catch { toast.error("Error al cargar clasificador"); }
  };

  const fetchCodigosPresupuestarios = async () => {
    try {
      const r = await axios.get(`${API}/api/definiciones/codigos-presupuestarios`, { headers });
      setCodigosPresupuestarios(r.data);
    } catch { setCodigosPresupuestarios([]); }
  };

  const fetchOficinas = async () => {
    try {
      const r = await axios.get(`${API}/api/definiciones/oficinas`, { headers });
      setOficinas(r.data);
    } catch { setOficinas([]); }
  };

  useEffect(() => {
    fetchClasificador();
  }, []);

  const openNew = () => {
    setEditingItem(null);
    setFormData({ extranet: false, descuento: false });
    fetchCodigosPresupuestarios();
    fetchOficinas();
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    fetchCodigosPresupuestarios();
    fetchOficinas();
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.tipo_clase_vehiculo) { toast.error("Tipo o Clase de Vehículo es obligatorio"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/api/vehiculos/clasificador/${editingItem.id}`, formData, { headers });
        toast.success("Clasificador actualizado");
      } else {
        await axios.post(`${API}/api/vehiculos/clasificador`, formData, { headers });
        toast.success("Clasificador creado");
      }
      setDialogOpen(false); setFormData({}); setEditingItem(null); fetchClasificador();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/vehiculos/clasificador/${itemToDelete.id}`, { headers });
      toast.success("Registro eliminado");
      setDeleteDialogOpen(false); setItemToDelete(null);
      fetchClasificador();
    } catch { toast.error("Error al eliminar"); }
  };

  const filteredClasificador = items.filter(i => {
    const cf = clasFilters;
    if (cf.codigo && !(i.codigo_presupuestario || "").toLowerCase().includes(cf.codigo.toLowerCase())) return false;
    if (cf.tipo && !(i.tipo_clase_vehiculo || "").toLowerCase().includes(cf.tipo.toLowerCase())) return false;
    if (cf.oficina && !(i.oficina || "").toLowerCase().includes(cf.oficina.toLowerCase())) return false;
    if (cf.tasa && !String(i.tasa || 0).includes(cf.tasa)) return false;
    if (cf.extranet && cf.extranet !== "todos") {
      const val = cf.extranet === "si";
      if (i.extranet !== val) return false;
    }
    if (cf.descuento && cf.descuento !== "todos") {
      const val = cf.descuento === "si";
      if (i.descuento !== val) return false;
    }
    return true;
  });

  // Export helpers
  const getClasExportRows = () => filteredClasificador.map(i => ({
    "Cod. Presupuestario": i.codigo_presupuestario || "",
    "Tipo o Clase de Vehículo": i.tipo_clase_vehiculo || "",
    "Oficina": i.oficina || "",
    "Tasa": i.tasa || 0,
    "Extranet": i.extranet ? "Sí" : "No",
    "Descuento": i.descuento ? `Sí (${i.porcentaje_descuento || 0}%)` : "No"
  }));

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportClasificadorExcel = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const data = getClasExportRows();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clasificador Vehículos");
    XLSX.writeFile(wb, `clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportClasificadorPDF = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const doc = new jsPDF("landscape");
    doc.setFontSize(16);
    doc.text("Clasificador de Vehículos", 14, 20);
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")}  |  Total: ${filteredClasificador.length} registros`, 14, 28);
    autoTable(doc, {
      head: [["Cod. Presupuestario", "Tipo o Clase de Vehículo", "Oficina", "Tasa", "Extranet", "Descuento"]],
      body: filteredClasificador.map(i => [
        i.codigo_presupuestario || "", i.tipo_clase_vehiculo || "", i.oficina || "",
        (i.tasa || 0).toFixed(2), i.extranet ? "Sí" : "No",
        i.descuento ? `Sí (${i.porcentaje_descuento || 0}%)` : "No"
      ]),
      startY: 35,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 64, 175] }
    });
    doc.save(`clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportClasificadorHTML = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const rows = getClasExportRows();
    const hdrs = Object.keys(rows[0]);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Clasificador de Vehículos</title><style>body{font-family:Arial,sans-serif}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#1e40af;color:#fff}tr:nth-child(even){background:#f2f2f2}h1{color:#1e40af}</style></head><body><h1>Clasificador de Vehículos</h1><p>Fecha: ${new Date().toLocaleDateString("es-VE")} | Total: ${rows.length} registros</p><table><thead><tr>${hdrs.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rows.map(r => `<tr>${hdrs.map(h => `<td>${r[h]}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`;
    downloadFile(html, `clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.html`, "text/html");
    toast.success("Exportado a HTML exitosamente");
  };

  const exportClasificadorCSV = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const rows = getClasExportRows();
    const hdrs = Object.keys(rows[0]);
    const csv = [hdrs.join(","), ...rows.map(r => hdrs.map(h => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))].join("\n");
    downloadFile(csv, `clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.csv`, "text/csv");
    toast.success("Exportado a CSV exitosamente");
  };

  const exportClasificadorText = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const rows = getClasExportRows();
    const hdrs = Object.keys(rows[0]);
    const text = [`CLASIFICADOR DE VEHÍCULOS`, `Fecha: ${new Date().toLocaleDateString("es-VE")}  |  Total: ${rows.length} registros`, "=".repeat(80), hdrs.join("\t"), "-".repeat(80), ...rows.map(r => hdrs.map(h => r[h]).join("\t"))].join("\n");
    downloadFile(text, `clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.txt`, "text/plain");
    toast.success("Exportado a Texto exitosamente");
  };

  const exportClasificadorJSON = () => {
    if (filteredClasificador.length === 0) { toast.error("No hay datos para exportar"); return; }
    const rows = getClasExportRows();
    downloadFile(JSON.stringify(rows, null, 2), `clasificador_vehiculos_${new Date().toISOString().split("T")[0]}.json`, "application/json");
    toast.success("Exportado a JSON exitosamente");
  };

  return (
    <div className="space-y-6" data-testid="clasificador-vehiculos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clasificador de Vehículos</h1>
          <p className="text-slate-500">Gestión del clasificador de vehículos</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredClasificador.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{items.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-exportar-clas-veh">
                <Download className="w-4 h-4" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportClasificadorHTML} className="gap-2 cursor-pointer">
                <FileCode className="w-4 h-4" />
                HTML
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClasificadorCSV} className="gap-2 cursor-pointer">
                <FileText className="w-4 h-4" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClasificadorText} className="gap-2 cursor-pointer">
                <File className="w-4 h-4" />
                Texto
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClasificadorExcel} className="gap-2 cursor-pointer">
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClasificadorPDF} className="gap-2 cursor-pointer">
                <FileText className="w-4 h-4" />
                PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportClasificadorJSON} className="gap-2 cursor-pointer">
                <FileJson className="w-4 h-4" />
                JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {(clasFilters.codigo || clasFilters.tipo || clasFilters.oficina || clasFilters.tasa || (clasFilters.extranet && clasFilters.extranet !== "todos") || (clasFilters.descuento && clasFilters.descuento !== "todos")) && (
            <Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setClasFilters({ codigo: "", tipo: "", oficina: "", tasa: "", extranet: "", descuento: "" })} data-testid="btn-limpiar-filtros-clas-veh">
              <FilterX className="w-4 h-4" />
              Limpiar Filtros
            </Button>
          )}
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo-clas-veh">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="px-3 py-2 text-left font-medium text-slate-600">Cod. Presupuestario</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Tipo o Clase de Vehículo</th>
              <th className="px-3 py-2 text-left font-medium text-slate-600">Oficina</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Tasa</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Extranet</th>
              <th className="px-3 py-2 text-center font-medium text-slate-600">Descuento</th>
              <th className="px-3 py-2 text-right font-medium text-slate-600">Acciones</th>
            </tr>
            <tr className="bg-slate-50/50 border-b">
              <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={clasFilters.codigo} onChange={(e) => setClasFilters({...clasFilters, codigo: e.target.value})} data-testid="filter-clas-codigo" /></th>
              <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={clasFilters.tipo} onChange={(e) => setClasFilters({...clasFilters, tipo: e.target.value})} data-testid="filter-clas-tipo" /></th>
              <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={clasFilters.oficina} onChange={(e) => setClasFilters({...clasFilters, oficina: e.target.value})} data-testid="filter-clas-oficina" /></th>
              <th className="px-3 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={clasFilters.tasa} onChange={(e) => setClasFilters({...clasFilters, tasa: e.target.value})} data-testid="filter-clas-tasa" /></th>
              <th className="px-3 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={clasFilters.extranet} onChange={(e) => setClasFilters({...clasFilters, extranet: e.target.value})} data-testid="filter-clas-extranet">
                  <option value="todos">Todos</option>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              </th>
              <th className="px-3 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={clasFilters.descuento} onChange={(e) => setClasFilters({...clasFilters, descuento: e.target.value})} data-testid="filter-clas-descuento">
                  <option value="todos">Todos</option>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              </th>
              <th className="px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {filteredClasificador.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No hay registros</td></tr>
            ) : filteredClasificador.map((item) => (
              <tr key={item.id} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-sm">{item.codigo_presupuestario || "-"}</td>
                <td className="px-3 py-2 text-sm font-medium">{item.tipo_clase_vehiculo || "-"}</td>
                <td className="px-3 py-2 text-sm">{item.oficina || "-"}</td>
                <td className="px-3 py-2 text-sm text-right">{(item.tasa || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.extranet ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                    {item.extranet ? "Si" : "No"}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  {item.descuento ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title={`${item.porcentaje_descuento || 0}% | ${item.fecha_descuento_desde || ""} - ${item.fecha_descuento_hasta || ""}`}>
                      Si ({item.porcentaje_descuento || 0}%)
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">No</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`btn-edit-clasveh-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} data-testid={`btn-delete-clasveh-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Dialog - Clasificador */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {editingItem ? "Editar Clasificador de Vehículos" : "Nuevo Clasificador de Vehículos"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Código Presupuestario</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-codigo-presup-clasveh">
                    {formData.codigo_presupuestario
                      ? (() => { const cp = codigosPresupuestarios.find(c => c.codigo_presupuestario === formData.codigo_presupuestario); return cp ? `${cp.codigo_presupuestario} - ${cp.nombre_partida || ""}` : formData.codigo_presupuestario; })()
                      : <span className="text-slate-500">Seleccione código</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar código o partida..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron códigos</CommandEmpty>
                      <CommandGroup>
                        {codigosPresupuestarios.map(cp => (
                          <CommandItem key={cp.id || cp.codigo_presupuestario} value={`${cp.codigo_presupuestario} ${cp.nombre_partida || ""}`} onSelect={() => setFormData({...formData, codigo_presupuestario: cp.codigo_presupuestario})}>
                            <Check className={`mr-2 h-4 w-4 ${formData.codigo_presupuestario === cp.codigo_presupuestario ? "opacity-100" : "opacity-0"}`} />
                            <span className="font-mono text-xs">{cp.codigo_presupuestario}</span>
                            <span className="ml-2 text-xs text-slate-500 truncate">{cp.nombre_partida || ""}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Tipo o Clase de Vehículo *</Label>
              <Input value={formData.tipo_clase_vehiculo || ""} onChange={(e) => setFormData({...formData, tipo_clase_vehiculo: e.target.value})} placeholder="Ej: Automóvil, Camioneta..." data-testid="form-tipo-clase-veh" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Oficina</Label>
                <Select value={formData.oficina || ""} onValueChange={(v) => setFormData({...formData, oficina: v})}>
                  <SelectTrigger data-testid="form-oficina-clasveh"><SelectValue placeholder="Seleccione oficina" /></SelectTrigger>
                  <SelectContent>
                    {oficinas.map((o) => <SelectItem key={o.id || o.nombre} value={o.nombre}>{o.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tasa</Label>
                <Input type="number" step="0.01" value={formData.tasa ?? ""} onChange={(e) => setFormData({...formData, tasa: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid="form-tasa-clasveh" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Extranet</Label>
                <Select value={formData.extranet ? "si" : "no"} onValueChange={(v) => setFormData({...formData, extranet: v === "si"})}>
                  <SelectTrigger data-testid="form-extranet-clasveh"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Si</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descuento</Label>
                <Select value={formData.descuento ? "si" : "no"} onValueChange={(v) => {
                  const has = v === "si";
                  setFormData({...formData, descuento: has, ...(!has ? { porcentaje_descuento: 0, fecha_descuento_desde: "", fecha_descuento_hasta: "" } : {})});
                }}>
                  <SelectTrigger data-testid="form-descuento-clasveh"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Si</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.descuento && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="space-y-2">
                  <Label>Porcentaje de Descuento (%)</Label>
                  <Input type="number" step="0.01" value={formData.porcentaje_descuento || ""} onChange={(e) => setFormData({...formData, porcentaje_descuento: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right max-w-[200px]" data-testid="form-porcentaje-desc-clasveh" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Desde</Label>
                    <Input type="date" value={formData.fecha_descuento_desde || ""} onChange={(e) => setFormData({...formData, fecha_descuento_desde: e.target.value})} data-testid="form-fecha-desde-clasveh" />
                  </div>
                  <div className="space-y-2">
                    <Label>Hasta</Label>
                    <Input type="date" value={formData.fecha_descuento_hasta || ""} onChange={(e) => setFormData({...formData, fecha_descuento_hasta: e.target.value})} data-testid="form-fecha-hasta-clasveh" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar-clas-veh">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar Eliminación</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">¿Está seguro de eliminar este clasificador?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete-clasveh">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiculosClasificador;
